import { cilHome } from "@coreui/icons";
import CIcon from "@coreui/icons-react";
import { CButton, CCol, CRow } from "@coreui/react";
import { ErrorMessage, Field, Form, Formik, FieldArray } from "formik";
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import adminAxiosInstance, { imageAxiosInstance } from "../../../config";
import errorImage from "../../assets/errorImage.jpg";
import * as Yup from "yup";
import Select from 'react-select';

import SweetAlert from "../../../helper/SweetAlert";

const PropertyEdit = () => {
  const [imageUrls, setImageUrls] = useState([]);
    const [data, setData] = useState(null);
    const [imageLoading, setImageLoading] = useState(false);
    const [agentSearchTerm, setAgentSearchTerm] = useState("");
    const [developerSearchTerm, setDeveloperSearchTerm] = useState("");
    const [agentSearchResults, setAgentSearchResults] = useState([]);
    const [developerSearchResults, setDeveloperSearchResults] = useState([]);
    const { id } = useParams();
    const navigate = useNavigate();
    const adminToken = localStorage.getItem("token");

  // Debounce function
  const debounce = (func, delay) => {
    let timeout;
    return function(...args) {
      const context = this;
      clearTimeout(timeout);
      timeout = setTimeout(() => func.apply(context, args), delay);
    };
  };

  const searchAgents = async (name) => {
    if (name.length < 2) {
      setAgentSearchResults([]);
      return;
    }
    try {
      const response = await adminAxiosInstance.get(`/property/getAgentAndDeveloper?name=${name}&type=agent`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      console.log("response", response?.data?.data);
      setAgentSearchResults(response?.data?.data || []);
    } catch (error) {
      console.error("Error searching agents:", error);
      setAgentSearchResults([]);
    }
  };

  const searchDevelopers = async (name) => {
    if (name.length < 2) {
      setDeveloperSearchResults([]);
      return;
    }
    try {
      const response = await adminAxiosInstance.get(`/property/getAgentAndDeveloper?name=${name}&type=developer`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setDeveloperSearchResults(response?.data?.data || []);
    } catch (error) {
      console.error("Error searching developers:", error);
      setDeveloperSearchResults([]);
    }
  };

  const debouncedAgentSearch = debounce(searchAgents, 500);
  const debouncedDeveloperSearch = debounce(searchDevelopers, 500);

  const propertyValidationSchema = Yup.object().shape({
    urlTitle: Yup.string().required("URL Title is required"),
    title: Yup.string().required("Title is required"),
    price: Yup.number().required("Price is required").min(0),
    priceCurrency: Yup.string().default("AED"),
    propertyType: Yup.string().oneOf(["apartment", "villa", "townhouse", "penthouse", "office", "plot"]).required("Property Type is required"),
    status: Yup.string().oneOf(["forSale", "forRent", "sold", "rented", "forBuy", "newProject", "commercial"]).required("Status is required"),
    bedrooms: Yup.number().min(0),
    bathrooms: Yup.number().min(0),
    maidRooms: Yup.number().min(0),
    propertySizeSqft: Yup.number().min(0),
    propertySizeSqm: Yup.number().min(0),
    serviceChargesAED: Yup.number().min(0),
    availableFrom: Yup.date().nullable(),
    highlights: Yup.array().of(Yup.string()),
    amenities: Yup.array().of(Yup.string().oneOf([
      "maidsRoom", "balcony", "security", "builtInWardrobes", "kitchenAppliances", "sharedPool",
      "centralAC", "coveredParking", "waterView", "landmarkView"
    ])),
    location: Yup.object().shape({
      address: Yup.string(),
      latitude: Yup.number(),
      longitude: Yup.number(),
      community: Yup.string(),
      subCommunity: Yup.string(),
    }),
    priceInsights: Yup.object().shape({
      averagePricePerSqft: Yup.number(),
      comparisonToArea: Yup.string(),
      similarPropertyComparison: Yup.string(),
    }),
    agent: Yup.string(), // Will be ObjectId in backend, but represented as string (ID) in frontend for selection
    developer: Yup.string(), // Will be ObjectId in backend, but represented as string (ID) in frontend for selection
    agentDetails: Yup.object().shape({
      name: Yup.string(),
      agencyName: Yup.string(),
      agencyLicense: Yup.string(),
      agentLicense: Yup.string(),
      phone: Yup.string(),
      whatsapp: Yup.string(),
      email: Yup.string().email("Invalid email"),
      responseTime: Yup.string(),
      rating: Yup.number(),
      totalProperties: Yup.number(),
    }),
    mortgage: Yup.object().shape({
      estimatedMonthlyPaymentAED: Yup.number(),
      interestRate: Yup.number(),
      downPaymentAED: Yup.number(),
      loanAmountAED: Yup.number(),
      loanPeriodYears: Yup.number(),
    }),
    images: Yup.array().of(
      Yup.object().shape({
        no: Yup.number(),
        url: Yup.string().url("Invalid URL"),
        isPrimary: Yup.boolean().default(false),
      })
    ),
    description: Yup.string(),
    listedDate: Yup.date().default(() => new Date()),
    updatedDate: Yup.date().default(() => new Date()),
    paymentMethod: Yup.string().oneOf(["cash", "mortgage", "installments"]),
    regulatoryInformation: Yup.object().shape({
      reference: Yup.string(),
      brokerLicense: Yup.string(),
      agencyName: Yup.string(),
      zoneName: Yup.string(),
      dldPermitNumber: Yup.string(),
      agentLicense: Yup.string(),
    }),
    // isDeleted: Yup.boolean().default(false),
  });

  const fetchData = async () => {
    if (!id) return;
    try {
      const response = await adminAxiosInstance.get(`/property/getById/${id}`, {
        headers: { Authorization: `Bearer ${adminToken}` },
      });
      setData(response?.data?.data);
      if (response?.data?.data?.images) {
        setImageUrls(response.data.data.images);
      }
    } catch (error) {
      console.log("error", error);
      SweetAlert({
        icon: "error",
        text: error?.response?.data?.message || "Error fetching property details",
        title: "Error",
      });
    }
  };

  const uploadImages = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setImageLoading(true);

    // Option 1: Upload one by one (recommended for now)
    const newUploadedImages = [];

    try {
      for (const file of files) {
        const formdata = new FormData();
        formdata.append("img", file);

        const response = await imageAxiosInstance.post("/upload", formdata, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        const uploaded = response?.data?.data;

        if (uploaded?.mediaLink) {
          newUploadedImages.push({
            no: imageUrls.length + newUploadedImages.length + 1,
            url: uploaded.mediaLink,
            isPrimary: imageUrls.length === 0 && newUploadedImages.length === 0,
          });
        }
      }

      setImageUrls((prev) => [...prev, ...newUploadedImages]);
    } catch (error) {
      console.error("Upload error:", error);
      SweetAlert({
        icon: "error",
        text: "Error uploading images",
        title: "Error",
      });
    } finally {
      setImageLoading(false);
    }
  };

  const removeImage = (index) => {
    const updatedImages = imageUrls.filter((_, i) => i !== index);
    setImageUrls(updatedImages);
  };

  const setPrimaryImage = (index) => {
    const updatedImages = imageUrls.map((img, i) => ({
      ...img,
      isPrimary: i === index,
    }));
    setImageUrls(updatedImages);
  };

  const handleFormSubmit = async (values) => {
    values.images = imageUrls;

    try {
      const url = id ? `/property/update/${id}` : "/property/add";
      const method = id ? "put" : "post";

      const response = await adminAxiosInstance[method](
        url,
        { ...values },
        {
          headers: { Authorization: `Bearer ${adminToken}` },
        }
      );
      SweetAlert({
        icon: "success",
        text: id ? "Property updated successfully!" : "Property created successfully!",
        title: "Success",
      });
      navigate("/admin/property");
    } catch (error) {
      console.log("error", error);
      SweetAlert({
        icon: "error",
        text: error?.response?.data?.message || "Error saving property",
        title: "Error",
      });
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (data?.agent) {
      adminAxiosInstance.get(`/agent/getById/${data.agent}`, { headers: { Authorization: `Bearer ${adminToken}` } })
        .then(res => setAgentSearchTerm(res.data.data.name))
        .catch(err => console.error("Error fetching agent details:", err));
    }
    if (data?.developer) {
      adminAxiosInstance.get(`/developer/getById/${data.developer}`, { headers: { Authorization: `Bearer ${adminToken}` } })
        .then(res => setDeveloperSearchTerm(res.data.data.name))
        .catch(err => console.error("Error fetching developer details:", err));
    }
  }, [data, adminToken]);

  return (
    <>
      <div className="d-flex align-items-center mb-3">
        <CIcon icon={cilHome} size="xxl" className="me-2" />
        <h3>{id ? "Edit Property" : "Add New Property"}</h3>
      </div>
      <div>
        <Link className="text-decoration-none text-white" to="/admin/property">
          <button className="text-white btn btn-info">Go Back</button>
        </Link>
      </div>

      <Formik
        enableReinitialize={true}
        initialValues={{
          urlTitle: data?.urlTitle || "",
          title: data?.title || "",
          price: data?.price || "",
          priceCurrency: data?.priceCurrency || "AED",
          propertyType: data?.propertyType || "apartment",
          status: data?.status || "forSale",
          bedrooms: data?.bedrooms || "",
          bathrooms: data?.bathrooms || "",
          maidRooms: data?.maidRooms || "",
          propertySizeSqft: data?.propertySizeSqft || "",
          propertySizeSqm: data?.propertySizeSqm || "",
          serviceChargesAED: data?.serviceChargesAED || "",
          availableFrom: data?.availableFrom ? new Date(data.availableFrom).toISOString().split('T')[0] : "",
          highlights: data?.highlights || [],
          amenities: data?.amenities || [],
          location: {
            address: data?.location?.address || "",
            latitude: data?.location?.latitude || "",
            longitude: data?.location?.longitude || "",
            community: data?.location?.community || "",
            subCommunity: data?.location?.subCommunity || "",
          },
          priceInsights: {
            averagePricePerSqft: data?.priceInsights?.averagePricePerSqft || "",
            comparisonToArea: data?.priceInsights?.comparisonToArea || "",
            similarPropertyComparison: data?.priceInsights?.similarPropertyComparison || "",
          },
          agent: data?.agent || "",
          developer: data?.developer || "",
          agentDetails: {
            name: data?.agentDetails?.name || "",
            agencyName: data?.agentDetails?.agencyName || "",
            agencyLicense: data?.agentDetails?.agencyLicense || "",
            agentLicense: data?.agentDetails?.agentLicense || "",
            phone: data?.agentDetails?.phone || "",
            whatsapp: data?.agentDetails?.whatsapp || "",
            email: data?.agentDetails?.email || "",
            responseTime: data?.agentDetails?.responseTime || "",
            rating: data?.agentDetails?.rating || 0,
            totalProperties: data?.agentDetails?.totalProperties || 0,
          },
          mortgage: {
            estimatedMonthlyPaymentAED: data?.mortgage?.estimatedMonthlyPaymentAED || "",
            interestRate: data?.mortgage?.interestRate || "",
            downPaymentAED: data?.mortgage?.downPaymentAED || "",
            loanAmountAED: data?.mortgage?.loanAmountAED || "",
            loanPeriodYears: data?.mortgage?.loanPeriodYears || "",
          },
          images: data?.images || [], // Images are handled by useState directly
          description: data?.description || "",
          listedDate: data?.listedDate ? new Date(data.listedDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          updatedDate: data?.updatedDate ? new Date(data.updatedDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          paymentMethod: data?.paymentMethod || "cash",
          regulatoryInformation: {
            reference: data?.regulatoryInformation?.reference || "",
            brokerLicense: data?.regulatoryInformation?.brokerLicense || "",
            agencyName: data?.regulatoryInformation?.agencyName || "",
            zoneName: data?.regulatoryInformation?.zoneName || "",
            dldPermitNumber: data?.regulatoryInformation?.dldPermitNumber || "",
            agentLicense: data?.regulatoryInformation?.agentLicense || "",
          },
          // isDeleted: data?.isDeleted || false,
        }}
        validationSchema={propertyValidationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ values, isSubmitting, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            {/* Basic Information */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Basic Information</h4>
              <CRow>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="urlTitle" className="fw-bold mb-2">
                      URL Title *
                    </label>
                    <Field
                      className="form-control"
                      name="urlTitle"
                      placeholder="e.g., vacant-marina-view-high-roi"
                      id="urlTitle"
                      onInput={(e) => 
                        // when space enter this '-' add this and only lower case 
                        setFieldValue("urlTitle", e.target.value.replace(/\s+/g, "-").toLowerCase())
                      }
                    />
                    <div className="text-danger">
                      <ErrorMessage name="urlTitle" />
                    </div>
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="title" className="fw-bold mb-2">
                      Title *
                    </label>
                    <Field
                      className="form-control"
                      name="title"
                      placeholder="e.g., Vacant | Marina View | High ROI"
                      id="title"
                    />
                    <div className="text-danger">
                      <ErrorMessage name="title" />
                    </div>
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="price" className="fw-bold mb-2">
                      Price *
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="price"
                      placeholder="3400000"
                      id="price"
                    />
                    <div className="text-danger">
                      <ErrorMessage name="price" />
                    </div>
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="propertyType" className="fw-bold mb-2">
                      Property Type *
                    </label>
                    <Field
                      as="select"
                      className="form-control"
                      name="propertyType"
                      id="propertyType"
                    >
                      <option value="apartment">Apartment</option>
                      <option value="villa">Villa</option>
                      <option value="plot">Plot</option>
                      <option value="townhouse">Townhouse</option>
                      <option value="penthouse">Penthouse</option>
                      <option value="office">Office</option>
                    </Field>
                    <div className="text-danger">
                      <ErrorMessage name="propertyType" />
                    </div>
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="status" className="fw-bold mb-2">
                      Status *
                    </label>
                    <Field
                      as="select"
                      className="form-control"
                      name="status"
                      id="status"
                    >
                      <option value="forSale">For Sale</option>
                      <option value="forRent">For Rent</option>
                      <option value="sold">Sold</option>
                      <option value="rented">Rented</option>
                      <option value="forBuy">For Buy</option>
                      <option value="newProject">New Project</option>
                      <option value="commercial">Commercial</option>
                    </Field>
                    <div className="text-danger">
                      <ErrorMessage name="status" />
                    </div>
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="priceCurrency" className="fw-bold mb-2">
                      Price Currency
                    </label>
                    <Field
                      as="select"
                      className="form-control"
                      name="priceCurrency"
                      id="priceCurrency"
                    >
                      <option value="AED">AED</option>
                      <option value="USD">USD</option>
                      <option value="GBP">GBP</option>
                    </Field>
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Property Details */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Property Details</h4>
              <CRow>
                <CCol sm={3}>
                  <div className="form-group mb-3">
                    <label htmlFor="bedrooms" className="fw-bold mb-2">
                      Bedrooms
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="bedrooms"
                      placeholder="3"
                      id="bedrooms"
                    />
                  </div>
                </CCol>
                <CCol sm={3}>
                  <div className="form-group mb-3">
                    <label htmlFor="bathrooms" className="fw-bold mb-2">
                      Bathrooms
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="bathrooms"
                      placeholder="2"
                      id="bathrooms"
                    />
                  </div>
                </CCol>
                <CCol sm={3}>
                  <div className="form-group mb-3">
                    <label htmlFor="maidRooms" className="fw-bold mb-2">
                      Maid Rooms
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="maidRooms"
                      placeholder="1"
                      id="maidRooms"
                    />
                  </div>
                </CCol>
                <CCol sm={3}>
                  <div className="form-group mb-3">
                    <label htmlFor="serviceChargesAED" className="fw-bold mb-2">
                      Service Charges (AED)
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="serviceChargesAED"
                      placeholder="15000"
                      id="serviceChargesAED"
                    />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="propertySizeSqft" className="fw-bold mb-2">
                      Size (sqft)
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="propertySizeSqft"
                      placeholder="1500"
                      id="propertySizeSqft"
                    />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="propertySizeSqm" className="fw-bold mb-2">
                      Size (sqm)
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="propertySizeSqm"
                      placeholder="139"
                      id="propertySizeSqm"
                    />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="availableFrom" className="fw-bold mb-2">
                      Available From
                    </label>
                    <Field
                      type="date"
                      className="form-control"
                      name="availableFrom"
                      id="availableFrom"
                    />
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Price Insights */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Price Insights</h4>
              <CRow>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="priceInsights.averagePricePerSqft" className="fw-bold mb-2">
                      Average Price Per Sqft
                    </label>
                    <Field
                      type="number"
                      className="form-control"
                      name="priceInsights.averagePricePerSqft"
                      placeholder="1200"
                      id="priceInsights.averagePricePerSqft"
                    />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="priceInsights.comparisonToArea" className="fw-bold mb-2">
                      Comparison To Area (e.g., "36% less than average")
                    </label>
                    <Field
                      type="text"
                      className="form-control"
                      name="priceInsights.comparisonToArea"
                      id="priceInsights.comparisonToArea"
                    />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="priceInsights.similarPropertyComparison" className="fw-bold mb-2">
                      Similar Property Comparison (e.g., "5% smaller than average size")
                    </label>
                    <Field
                      type="text"
                      className="form-control"
                      name="priceInsights.similarPropertyComparison"
                      id="priceInsights.similarPropertyComparison"
                    />
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Location */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Location</h4>
              <CRow>
                <CCol sm={12}>
                  <div className="form-group mb-3">
                    <label htmlFor="location.address" className="fw-bold mb-2">
                      Address
                    </label>
                    <Field
                      className="form-control"
                      name="location.address"
                      placeholder="Murjan 1, Jumeirah Beach Residence, Dubai"
                      id="location.address"
                    />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="location.community" className="fw-bold mb-2">
                      Community
                    </label>
                    <Field
                      className="form-control"
                      name="location.community"
                      placeholder="Jumeirah Beach Residence"
                      id="location.community"
                    />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="location.subCommunity" className="fw-bold mb-2">
                      Sub Community
                    </label>
                    <Field
                      className="form-control"
                      name="location.subCommunity"
                      placeholder="Murjan 1"
                      id="location.subCommunity"
                    />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="location.latitude" className="fw-bold mb-2">
                      Latitude
                    </label>
                    <Field
                      type="number"
                      step="any"
                      className="form-control"
                      name="location.latitude"
                      placeholder="25.0772"
                      id="location.latitude"
                    />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="location.longitude" className="fw-bold mb-2">
                      Longitude
                    </label>
                    <Field
                      type="number"
                      step="any"
                      className="form-control"
                      name="location.longitude"
                      placeholder="55.1372"
                      id="location.longitude"
                    />
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Agent & Developer */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Agent & Developer</h4>
              <CRow>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentSearch" className="fw-bold mb-2">
                      Search Agent
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="agentSearch"
                      placeholder="Search by agent name"
                      value={agentSearchTerm}
                      onChange={(e) => {
                        setAgentSearchTerm(e.target.value);
                        debouncedAgentSearch(e.target.value);
                      }}
                    />
                    {agentSearchResults.length > 0 && (
                      <select
                        className="form-control mt-2"
                        onChange={(e) => {
                          setFieldValue("agent", e.target.value);
                          setAgentSearchTerm(agentSearchResults.find(agent => agent._id === e.target.value)?.name || "");
                          setAgentSearchResults([]);
                        }}
                        value={values.agent}
                      >
                        <option value="">Select an Agent</option>
                        {agentSearchResults.map((agent) => (
                          <option key={agent._id} value={agent._id}>
                            {agent.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <div className="form-group mb-3 mt-3">
                      <label htmlFor="agent" className="fw-bold mb-2">
                        Selected Agent ID
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        name="agent"
                        id="agent"
                        placeholder="Agent ID (auto-filled or enter manually)"
                        readOnly
                      />
                    </div>
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="developerSearch" className="fw-bold mb-2">
                      Search Developer
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      id="developerSearch"
                      placeholder="Search by developer name"
                      value={developerSearchTerm}
                      onChange={(e) => {
                        setDeveloperSearchTerm(e.target.value);
                        debouncedDeveloperSearch(e.target.value);
                      }}
                    />
                                        {developerSearchResults.length > 0 && (
                      <select
                        className="form-control mt-2"
                        onChange={(e) => {
                          setFieldValue("developer", e.target.value);
                          setDeveloperSearchTerm(developerSearchResults.find(dev => dev._id === e.target.value)?.name || "");
                          setDeveloperSearchResults([]);
                        }}
                        value={values.developer}
                      >
                        <option value="">Select a Developer</option>
                        {developerSearchResults.map((developer) => (
                          <option key={developer._id} value={developer._id}>
                            {developer.name}
                          </option>
                        ))}
                      </select>
                    )}
                    <div className="form-group mb-3 mt-3">
                      <label htmlFor="developer" className="fw-bold mb-2">
                        Selected Developer ID
                      </label>
                      <Field
                        type="text"
                        className="form-control"
                        name="developer"
                        id="developer"
                        placeholder="Developer ID (auto-filled or enter manually)"
                        readOnly
                      />
                    </div>
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Agent Details (Nested) */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Agent Details (Optional)</h4>
              <CRow>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.name" className="fw-bold mb-2">Agent Name</label>
                    <Field type="text" className="form-control" name="agentDetails.name" id="agentDetails.name" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.agencyName" className="fw-bold mb-2">Agency Name</label>
                    <Field type="text" className="form-control" name="agentDetails.agencyName" id="agentDetails.agencyName" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.agencyLicense" className="fw-bold mb-2">Agency License</label>
                    <Field type="text" className="form-control" name="agentDetails.agencyLicense" id="agentDetails.agencyLicense" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.agentLicense" className="fw-bold mb-2">Agent License</label>
                    <Field type="text" className="form-control" name="agentDetails.agentLicense" id="agentDetails.agentLicense" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.phone" className="fw-bold mb-2">Phone</label>
                    <Field type="text" className="form-control" name="agentDetails.phone" id="agentDetails.phone" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.whatsapp" className="fw-bold mb-2">Whatsapp</label>
                    <Field type="text" className="form-control" name="agentDetails.whatsapp" id="agentDetails.whatsapp" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.email" className="fw-bold mb-2">Email</label>
                    <Field type="email" className="form-control" name="agentDetails.email" id="agentDetails.email" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.responseTime" className="fw-bold mb-2">Response Time</label>
                    <Field type="text" className="form-control" name="agentDetails.responseTime" id="agentDetails.responseTime" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.rating" className="fw-bold mb-2">Rating</label>
                    <Field type="number" className="form-control" name="agentDetails.rating" id="agentDetails.rating" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="agentDetails.totalProperties" className="fw-bold mb-2">Total Properties</label>
                    <Field type="number" className="form-control" name="agentDetails.totalProperties" id="agentDetails.totalProperties" />
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Mortgage Information */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Mortgage Information</h4>
              <CRow>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="mortgage.estimatedMonthlyPaymentAED" className="fw-bold mb-2">Estimated Monthly Payment (AED)</label>
                    <Field type="number" className="form-control" name="mortgage.estimatedMonthlyPaymentAED" id="mortgage.estimatedMonthlyPaymentAED" />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="mortgage.interestRate" className="fw-bold mb-2">Interest Rate (%)</label>
                    <Field type="number" step="any" className="form-control" name="mortgage.interestRate" id="mortgage.interestRate" />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="mortgage.downPaymentAED" className="fw-bold mb-2">Down Payment (AED)</label>
                    <Field type="number" className="form-control" name="mortgage.downPaymentAED" id="mortgage.downPaymentAED" />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="mortgage.loanAmountAED" className="fw-bold mb-2">Loan Amount (AED)</label>
                    <Field type="number" className="form-control" name="mortgage.loanAmountAED" id="mortgage.loanAmountAED" />
                  </div>
                </CCol>
                <CCol sm={4}>
                  <div className="form-group mb-3">
                    <label htmlFor="mortgage.loanPeriodYears" className="fw-bold mb-2">Loan Period (Years)</label>
                    <Field type="number" className="form-control" name="mortgage.loanPeriodYears" id="mortgage.loanPeriodYears" />
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Dates */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Dates</h4>
              <CRow>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="listedDate" className="fw-bold mb-2">Listed Date</label>
                    <Field type="date" className="form-control" name="listedDate" id="listedDate" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="updatedDate" className="fw-bold mb-2">Updated Date</label>
                    <Field type="date" className="form-control" name="updatedDate" id="updatedDate" />
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Payment Method */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Payment Method</h4>
              <CRow>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="paymentMethod" className="fw-bold mb-2">Payment Method</label>
                    <Field as="select" className="form-control" name="paymentMethod" id="paymentMethod">
                      <option value="cash">Cash</option>
                      <option value="mortgage">Mortgage</option>
                      <option value="installments">Installments</option>
                    </Field>
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Regulatory Information */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Regulatory Information</h4>
              <CRow>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="regulatoryInformation.reference" className="fw-bold mb-2">Reference</label>
                    <Field type="text" className="form-control" name="regulatoryInformation.reference" id="regulatoryInformation.reference" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="regulatoryInformation.brokerLicense" className="fw-bold mb-2">Broker License</label>
                    <Field type="text" className="form-control" name="regulatoryInformation.brokerLicense" id="regulatoryInformation.brokerLicense" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="regulatoryInformation.agencyName" className="fw-bold mb-2">Agency Name</label>
                    <Field type="text" className="form-control" name="regulatoryInformation.agencyName" id="regulatoryInformation.agencyName" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="regulatoryInformation.zoneName" className="fw-bold mb-2">Zone Name</label>
                    <Field type="text" className="form-control" name="regulatoryInformation.zoneName" id="regulatoryInformation.zoneName" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="regulatoryInformation.dldPermitNumber" className="fw-bold mb-2">DLD Permit Number</label>
                    <Field type="text" className="form-control" name="regulatoryInformation.dldPermitNumber" id="regulatoryInformation.dldPermitNumber" />
                  </div>
                </CCol>
                <CCol sm={6}>
                  <div className="form-group mb-3">
                    <label htmlFor="regulatoryInformation.agentLicense" className="fw-bold mb-2">Agent License</label>
                    <Field type="text" className="form-control" name="regulatoryInformation.agentLicense" id="regulatoryInformation.agentLicense" />
                  </div>
                </CCol>
              </CRow>
            </div>

            {/* Amenities */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Amenities</h4>
              <CRow>
                {[  "maidsRoom",
                    "balcony",
                    "security",
                    "builtInWardrobes",
                    "kitchenAppliances",
                    "sharedPool",
                    "centralAC",
                    "coveredParking",
                    "waterView",
                    "landmarkView",
                  ].map((amenity) => (
                  <CCol sm={4} key={amenity}>
                    <div className="form-check mb-2">
                      <Field
                        type="checkbox"
                        className="form-check-input"
                        name="amenities"
                        value={amenity}
                        id={`amenity-${amenity}`}
                      />
                      <label className="form-check-label" htmlFor={`amenity-${amenity}`}>
                        {amenity.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                      </label>
                    </div>
                  </CCol>
                ))}
              </CRow>
            </div>

            {/* Highlights */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Highlights</h4>
              <FieldArray name="highlights">
                {({ push, remove }) => (
                  <>
                    {values.highlights.map((highlight, index) => (
                      <CRow key={index} className="mb-2">
                        <CCol sm={10}>
                          <Field
                            className="form-control"
                            name={`highlights.${index}`}
                            placeholder="Enter highlight"
                          />
                        </CCol>
                        <CCol sm={2}>
                          <CButton
                            color="danger"
                            onClick={() => remove(index)}
                          >
                            Remove
                          </CButton>
                        </CCol>
                      </CRow>
                    ))}
                    <CButton
                      color="success"
                      onClick={() => push("")}
                    >
                      Add Highlight
                    </CButton>
                  </>
                )}
              </FieldArray>
            </div>



            {/* Images */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Property Images</h4>
              <div className="form-group mb-3">
                <label htmlFor="images" className="fw-bold mb-2">
                  Upload Images
                </label>
                <input
                  type="file"
                  id="images"
                  className="form-control"
                  multiple
                  onChange={uploadImages}
                  accept="image/*"
                  disabled={imageLoading}
                />
                {imageLoading && <small className="text-info">Uploading images...</small>}
              </div>
              <CRow>
                {imageUrls.map((img, index) => (
                  <CCol sm={3} key={index} className="mb-3">
                    <div className="position-relative">
                      <img
                        src={img.url || errorImage}
                        alt={`Property ${index + 1}`}
                        className="img-thumbnail w-100"
                        style={{ height: "200px", objectFit: "cover" }}
                      />
                      <div className="mt-2">
                        <button
                          type="button"
                          className="btn btn-sm btn-danger me-2"
                          onClick={() => removeImage(index)}
                        >
                          Remove
                        </button>
                        <button
                          type="button"
                          className={`btn btn-sm ${img.isPrimary ? "btn-success" : "btn-outline-success"}`}
                          onClick={() => setPrimaryImage(index)}
                        >
                          {img.isPrimary ? "Primary" : "Set Primary"}
                        </button>
                      </div>
                    </div>
                  </CCol>
                ))}
              </CRow>
            </div>

            {/* Description */}
            <div className="border-top border-info border-3 bg-white mt-3 p-3">
              <h4 className="fw-normal mb-3">Description</h4>
              <Field
                as="textarea"
                className="form-control"
                name="description"
                rows={6}
                placeholder="Enter property description..."
              />
            </div>

            {/* Submit Button */}
            <div className="mt-3">
              <CButton
                type="submit"
                className="btn btn-info text-white"
                disabled={isSubmitting || imageLoading}
              >
                {isSubmitting ? "Please wait..." : id ? "Update Property" : "Create Property"}
              </CButton>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default PropertyEdit;